"use client";
import React, { useState, useEffect } from "react";
// Usamos los mismos estilos de Programas Académicos para replicar el diseño
import styles from "../../styles/PantallaDeProgramasAcademicos.v2.module.css";
import estudiantesStyles from "../../styles/EstudiantesStyles/Estudiantes.module.css";
import HeaderGlobal from '../../components/HeaderGlobal';
import Sidebar from "../../components/Sidebar";
import GroupsTable from "./GroupsTable";
import LoaderOverlay from '../../components/LoaderOverlay';
import NuevoGrupoModal from "./NuevoGrupoModal";
import { usePermissions } from '../../hooks/usePermissions';
import { useAuth } from '../../api/AuthContext';

function PantallaDeGrupos() {
  const { canCreate } = usePermissions();
  const { refreshMe } = useAuth();
  const puedeCrear = canCreate('Perfil Académico');

  // Refrescar permisos automáticamente al entrar a la pantalla
  React.useEffect(() => {
    refreshMe();
  }, [refreshMe]);

  const [openNew, setOpenNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [queryDebounced, setQueryDebounced] = useState("");
  const [selectedCampus, setSelectedCampus] = useState(() => localStorage.getItem('selectedCampus') || '');
  const [campusIdFilter, setCampusIdFilter] = useState(null);
  const [cicloIdFilter, setCicloIdFilter] = useState(null);
  const [ciclos, setCiclos] = useState([]);
  const handleCreated = () => {
    // Mismo comportamiento que en ciclos: mostrar ventana de confirmación
    try {
      window.alert('Grupo guardado correctamente');
    } catch (_) {
      // En entornos sin window (tests, SSR) simplemente ignorar
    }
  };

  const handleSearch = () => {
    // Forzar que el término actual dispare la búsqueda de inmediato
    setQueryDebounced(query);
  };

  // Resolver id_campus a partir del nombre de campus seleccionado en el HeaderGlobal
  useEffect(() => {
    let cancelled = false;

    const resolveCampusId = async () => {
      if (!selectedCampus || selectedCampus === 'Todos') {
        setCampusIdFilter(null);
        return;
      }
                                                                
      try { 
        const base = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';
        const res = await fetch(`${base}/catalogos/campus/`);
        const data = await res.json();
        if (!Array.isArray(data)) {
          if (!cancelled) setCampusIdFilter(null);
          return;
        }
        const campusObj = data.find(c => c.nombre_campus === selectedCampus);
        if (!cancelled) {
          setCampusIdFilter(campusObj ? campusObj.id_campus : null);
        }
      } catch (e) {
        if (!cancelled) setCampusIdFilter(null);
      }
    };

    resolveCampusId();
    return () => { cancelled = true; };
  }, [selectedCampus]);

  // Debounce para el buscador, similar a AsignacionBecasScreen
  useEffect(() => {
    const handler = setTimeout(() => {
      setQueryDebounced(query);
    }, 400);
    return () => clearTimeout(handler);
  }, [query]);

  // Cargar ciclos escolares en orden descendente
  useEffect(() => {
    let cancelled = false;
    const base = process.env.REACT_APP_API_BASE || 'http://localhost:8000/api';
    const token = localStorage.getItem('authToken');
    fetch(`${base}/catalogos/ciclos-escolares/?ordering=-anio,-numero_periodo&page_size=100`, {
      headers: { 'Content-Type': 'application/json', ...(token ? { Authorization: `Bearer ${token}` } : {}) },
    })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        if (cancelled || !data) return;
        const lista = Array.isArray(data) ? data : (data.results || []);
        setCiclos(lista);
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);
  return (
    <div className={styles.mainContainer}>
      <div className={styles.layout}>
        <div className={styles.sidebarColumn}>
          <Sidebar />
        </div>
        <div className={styles.contentColumn}>
          <main className={styles.mainContent}>
            <HeaderGlobal
              title="Grupos"
              campus={selectedCampus}
              onCampusChange={e => {
                const value = e?.target?.value ?? '';
                setSelectedCampus(value);
              }}
            />
            <div className={styles.contentArea}>
              <div className={styles.contentWrapper} style={{ position: 'relative' }}>
                <LoaderOverlay visible={loading} label="Cargando grupos..." />

                <div className={estudiantesStyles.searchSection}>
                  <div className={estudiantesStyles.searchCard}>
                    <div className={styles.programsSearchRow}>
                      <div className={estudiantesStyles.searchInputGroup}>
                        <svg
                          className={estudiantesStyles.searchIcon}
                          width="20"
                          height="20"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M21 21L15 15M17 10C17 13.866 13.866 17 10 17C6.13401 17 3 13.866 3 10C3 6.13401 6.13401 3 10 3C13.866 3 17 6.13401 17 10Z"
                            stroke="#898d8d"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                        <input
                          type="text"
                          className={estudiantesStyles.searchInput}
                          placeholder="Buscar Grupo, Materia o Asesor..."
                          value={query}
                          onChange={(e) => setQuery(e.target.value)}
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') handleSearch();
                          }}
                          aria-label="Buscar grupo, materia o asesor"
                        />
                      </div>

                      <select
                        style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid #d1d5db', fontSize: '14px', color: '#374151', background: '#fff', minWidth: '160px' }}
                        value={cicloIdFilter || ''}
                        onChange={(e) => setCicloIdFilter(e.target.value || null)}
                        aria-label="Filtrar por ciclo escolar"
                      >
                        <option value="">-- Todos los ciclos --</option>
                        {ciclos.map((c) => {
                          const cicloId = c.id || c.id_ciclo_escolar;
                          return (
                            <option key={cicloId} value={cicloId}>
                              {c.nombre_ciclo || c.nombre}
                            </option>
                          );
                        })}
                      </select>

                      <button
                        className={styles.buscarButtonProgramas}
                        onClick={handleSearch}
                      >
                        Buscar
                      </button>

                      {puedeCrear && (
                        <button
                          className={styles.newProgramButton}
                          aria-label="Crear nuevo grupo"
                          onClick={() => setOpenNew(true)}
                        >
                          + Nuevo Grupo
                        </button>
                      )}
                    </div>
                  </div>
                </div>
                {/* Tabla de grupos, controla el loader igual que Programas Académicos */}
                <GroupsTable
                  onLoadingChange={setLoading}
                  query={queryDebounced}
                  campusFilter={selectedCampus}
                  campusIdFilter={campusIdFilter}
                  cicloIdFilter={cicloIdFilter}
                />
              </div>
            </div>
          </main>
        </div>
      </div>
  <NuevoGrupoModal open={openNew} onClose={() => setOpenNew(false)} onCreated={handleCreated} />
    </div>
  );
}

export default PantallaDeGrupos;
